/*
 * EmployeeBannerBlocker — fullscreen overlay shown right after login. The
 * employee sees the image, clicks "Ciente" for each pending banner, then the
 * dashboard content becomes reachable. Banners already acknowledged are not
 * fetched by the backend, so this component naturally disappears once the
 * employee has cleared them.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function EmployeeBannerBlocker({ onAllDone }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState(0);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee/banners/pending`, {
        params: { token }, timeout: 15000,
      });
      const list = r.data?.items || [];
      setItems(list);
      setCurrent(0);
      if (list.length === 0) onAllDone?.();
    } catch {
      // Fail open — never block the portal because of a network hiccup.
      setItems([]);
      onAllDone?.();
    } finally { setLoading(false); }
  }, [token, onAllDone]);

  useEffect(() => { load(); }, [load]);

  const ack = async () => {
    const b = items[current];
    if (!b) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/employee/banners/${b.id}/acknowledge`,
        { token }, { timeout: 15000 });
      // Move to next banner or unblock the portal
      if (current + 1 >= items.length) {
        onAllDone?.();
        setItems([]);
      } else {
        setCurrent((c) => c + 1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao registrar Ciente.");
    } finally { setSubmitting(false); }
  };

  if (loading || items.length === 0) return null;

  const b = items[current];
  const imgUrl = `${API}/employee/banners/${b.id}/image?token=${encodeURIComponent(token || "")}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-slate-900/85 backdrop-blur flex flex-col items-center justify-center p-4"
        data-testid="banner-blocker"
      >
        <motion.div
          key={b.id}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {b.title && (
            <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-fuchsia-50 to-pink-50">
              <p className="text-[11px] font-semibold text-fuchsia-700 uppercase tracking-wider">
                Comunicado {items.length > 1
                  ? `${current + 1}/${items.length}` : ""}
              </p>
              <h2 className="text-base sm:text-lg font-heading font-bold text-slate-900">
                {b.title}
              </h2>
            </div>
          )}
          <div className="bg-slate-900 flex items-center justify-center p-2">
            <img src={imgUrl}
                  alt={b.title || "Banner"}
                  className="w-full max-h-[65vh] object-contain rounded"
                  data-testid={`banner-image-${b.id}`} />
          </div>
          <div className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50">
            <p className="text-xs text-slate-600 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Ao clicar em Ciente você confirma que leu este aviso. Seu Ciente
              fica registrado com data, hora e IP.
            </p>
            <Button
              onClick={ack}
              disabled={submitting}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg min-w-[160px]"
              data-testid={`banner-ack-btn-${b.id}`}
            >
              {submitting
                ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Enviando…</>
                : <><CheckCircle2 className="w-5 h-5 mr-2" />Ciente</>}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
